/**
 * The poke pop-ups (2026-10-29): every poke that pops up waits in a sticky
 * stack, newest first, until you close it (pokes never dismiss themselves).
 * Silent pokes (`nx_silent`: from someone you ignore, or while you are on
 * Do Not Disturb) and pokes from someone ignored here never pop up or buzz;
 * the server keeps them in your poke list all the same.
 */
import type { GatewayDispatchEvents, User } from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { FSI, PDI } from '../i18n/core';
import { useData } from '../store/data';
import { activeGuildId, displayName } from '../store/selectors';
import { isIgnored } from '../store/um';
import { announce } from '../ui/overlay';
import { alertPoke, closePokeNotice, stopTitleFlash } from './alert';

export type NxPokeData = GatewayDispatchEvents['NX_POKE'];

export interface StackPoke {
  /** The poke's id, or one made here when an older server sent none. */
  key: string;
  from: User;
  message: string | null;
  /** When it was sent (ms). */
  at: number;
}

/** More than this and the oldest goes (they are rate-limited, but many people may poke at once). */
export const POKE_STACK_MAX = 30;

interface PokeStackStore {
  pokes: StackPoke[];
}

export const usePokeStack = create<PokeStackStore>(() => ({ pokes: [] }));

let seq = 0;

/** A poke from the gateway: onto the stack, with its alert, unless it is silent. */
export function receivePoke(d: NxPokeData): boolean {
  if (d.nx_silent || isIgnored(d.from.id)) return false;
  const key = d.id ?? `local-${++seq}`;
  if (usePokeStack.getState().pokes.some((p) => p.key === key)) return false;
  const parsed = Date.parse(d.created_at);
  const poke: StackPoke = {
    key,
    from: d.from,
    message: d.message?.trim() ? d.message : null,
    at: Number.isNaN(parsed) ? Date.now() : parsed,
  };
  usePokeStack.setState((s) => ({ pokes: [poke, ...s.pokes].slice(0, POKE_STACK_MAX) }));
  const { t } = tr();
  const who = senderName(poke.from);
  alertPoke({
    key,
    from: poke.from.id,
    title: t('pk.title', { who }),
    body: poke.message ?? t('pk.os.body'),
  });
  // the message is the sender's own text: isolated like a name
  announce(
    poke.message
      ? `${t('ns.poke.got', { who })}: “${FSI}${poke.message}${PDI}”`
      : t('ns.poke.got', { who }),
  );
  return true;
}

/** Closes one poke's card (and its OS notification). */
export function closePoke(key: string): void {
  closePokeNotice(key);
  usePokeStack.setState((s) => ({ pokes: s.pokes.filter((p) => p.key !== key) }));
  if (!usePokeStack.getState().pokes.length) stopTitleFlash();
}

/** Closes every card. */
export function closeAllPokes(): void {
  for (const p of usePokeStack.getState().pokes) closePokeNotice(p.key);
  usePokeStack.setState({ pokes: [] });
  stopTitleFlash();
}

/** The sender as the server shows them: their nickname here, else their display name. */
export function senderName(from: User, d = useData.getState()): string {
  const gid = activeGuildId(d);
  const member = gid ? d.members[gid]?.[from.id] : undefined;
  return displayName(d.users[from.id] ?? from, member) || from.username;
}
